import { BarcodeApiClient } from '@jbhi-fi/apis/src/barcode/BarcodeApiClient';
import { serviceHelper } from '../../config/service-helper';
import { Coupon, WalletCoupon } from '@jbhi-fi/components/src/types';
import { ProductExclusions, CartCoupon } from './types';
import { Exclusions } from '@jbhi-fi/apis/src/onlineCouponsApi/types';

const barcodeApiClient = new BarcodeApiClient(`${serviceHelper.themeApisUrl}/barcode`);
const getValidWalletCouponRefs = (
  couponsInWallet: Array<WalletCoupon> = [],
  appliedCoupons: Array<CartCoupon> = [],
  isCartPage: boolean
): Set<string> | null => {
  const spendAndGetAppliedRefs = isCartPage
    ? new Set(appliedCoupons.filter(c => c.type === 'SpendAndGet').map(c => c.code))
    : [];
  const walletRefs = couponsInWallet.filter(w => w.CouldBeApplied).map(w => w.Reference);
  const combined = [...walletRefs, ...spendAndGetAppliedRefs];
  return combined.length > 0 ? new Set(combined) : null;
};

const getFilteredCoupons = (
  coupons: Array<Coupon>,
  appliedCoupons: Array<CartCoupon>,
  isCartPage: boolean,
  couponsInWallet?: Array<WalletCoupon>
) => {
  const today = new Date().setHours(0, 0, 0, 0);
  const validWalletCouponRefs = getValidWalletCouponRefs(couponsInWallet, appliedCoupons, isCartPage);

  return coupons
    ?.filter(coupon => {
      const isValid = !coupon.redemptionDate && new Date(coupon.expiryDate).setHours(0, 0, 0, 0) >= today;
      if (validWalletCouponRefs) {
        return isValid && validWalletCouponRefs.has(coupon.couponCode);
      }
      return isValid;
    })
    .map(coupon => {
      const appliedCoupon = appliedCoupons?.find(c => c.code === coupon.couponCode);
      return {
        ...coupon,
        type: coupon.type || appliedCoupon?.type,
        minimumSpend: coupon.minimumSpend || appliedCoupon?.minimumSpend,
        isApplied: !!appliedCoupon,
        success: !!appliedCoupon?.success,
        applicableLineItemIds: appliedCoupon?.applicableLineItemIds,
      };
    });
};

const getApplicableCoupons = (
  coupons: Array<Coupon>,
  exclusionsMap: Map<string, Exclusions>,
  product?: ProductExclusions
): Array<Coupon> => {
  if (!product) return coupons;

  return coupons.filter((coupon: Coupon) => {
    const exclusion = exclusionsMap.get(coupon.couponExternalId.toString());
    if (!exclusion) return false;

    const hasMatchingCondition = (conditionType: 'include' | 'exclude', identifierType: string, value: string) =>
      exclusion.redemptionConditions.some(
        condition =>
          condition.conditionType.toLowerCase() === conditionType.toLowerCase() &&
          condition.identifierType.toLowerCase() === identifierType.toLowerCase() &&
          condition.identifier.includes(value?.toString())
      );

    const isDepartmentCodeExcluded = hasMatchingCondition('exclude', 'DepartmentCode', product.departmentCode);
    const isDepartmentCodeIncluded = hasMatchingCondition('include', 'DepartmentCode', product.departmentCode);

    const isSupplierExcluded = hasMatchingCondition('exclude', 'SupplierNumber', product.supplierNumber);

    const isSkuExcluded = hasMatchingCondition('exclude', 'Sku', product.sku);

    const isProductGroupExcluded = hasMatchingCondition('exclude', 'ProductGroupId', product.productGroupId);

    const isPreOrderOrMarketplace = product.isPreOrder || product.isMarketplace;

    const isExcluded =
      isDepartmentCodeExcluded ||
      isPreOrderOrMarketplace ||
      isSupplierExcluded ||
      isSkuExcluded ||
      isProductGroupExcluded ||
      !isDepartmentCodeIncluded;

    return !(isExcluded && coupon.couponReference.toLowerCase().includes('perks'));
  });
};
const generateCouponBarcodes = async (couponsInput: Array<Coupon>) => {
  const newBarcodeMap = new Map<string, string | undefined>();
  const barcodePromises = couponsInput.map(async coupon => {
    try {
      const formattedCode = `#${coupon.couponCode}`;
      const response = await barcodeApiClient.generateBarcode({ couponCode: formattedCode });
      const barcodeBase64 = response?.data?.base64;
      newBarcodeMap.set(coupon.couponCode, barcodeBase64);
    } catch (error) {
      console.error('Error generating barcode for coupon:', coupon.couponCode, error);
    }
  });
  await Promise.all(barcodePromises);

  return newBarcodeMap;
};

const getCouponDiscounts = (coupons: Array<Coupon>, couponsInWallet: Array<WalletCoupon>) => {
  const validWalletCouponsMap = new Map(couponsInWallet.map(w => [w.Reference, w.DiscountInc ?? 0]));

  const enrichedCoupons = coupons.map(coupon => {
    const walletDiscount = validWalletCouponsMap.get(coupon.couponCode);
    return {
      ...coupon,
      discount: walletDiscount ? walletDiscount : coupon.discount ?? 0,
    };
  });
  return enrichedCoupons;
};

const sortApplicableCouponsByDiscount = (coupons: Array<Coupon>, couponsInWallet: Array<WalletCoupon>) => {
  if (!Array.isArray(couponsInWallet) || couponsInWallet.length === 0) return coupons;
  const enrichedCoupons = getCouponDiscounts(coupons, couponsInWallet);

  return enrichedCoupons
    .sort((a, b) => b.discount - a.discount)
    .map((coupon, index) => ({
      ...coupon,
      isBestDeal: index === 0,
    }));
};

const mapCartCouponToCoupon = (
  appliedCoupons: Array<CartCoupon>,
  couponHandles: Array<string | undefined>,
  isLoggedIn: boolean
) => {
  const couponsToMap =
    isLoggedIn && couponHandles.length > 0
      ? appliedCoupons.filter(coupon => coupon.action === 'manual' && !couponHandles.includes(coupon.coupon))
      : appliedCoupons;

  return couponsToMap.map(coupon => ({
    ...coupon,
    couponName: coupon.name || '',
    couponReference: coupon.coupon || '',
    couponDescription: coupon.message || '',
    couponCode: coupon.code || '',
    couponType: coupon.type,
    success: coupon.success !== undefined ? coupon.success : false,
    discount: coupon.amount !== undefined ? coupon.amount : 0,
    couponExternalId: coupon.externalId !== undefined ? coupon.externalId : 0,
    content: {
      displayName: coupon.name || '',
      termsAndConditions: null,
    },
    allocatedDate: '',
    expiryDate: '',
    isApplied: true,
    isBestDeal: false,
  }));
};

export {
  generateCouponBarcodes,
  getFilteredCoupons,
  getCouponDiscounts,
  getApplicableCoupons,
  sortApplicableCouponsByDiscount,
  mapCartCouponToCoupon,
};
